import { useEffect, useMemo, useState } from 'react';
import { first } from 'lodash';
import { isInEnum } from 'App/Activity/components/Utility';
import {
	LaunchAction,
	MachineAction,
	MachineData,
	Session,
	SessionAction,
	useResourceManagerContext,
} from 'App/Activity/ResourceManagerContext';
import { useDesktopActions } from 'App/Activity/useDesktopActions';
import { FeatureFlag } from 'Environment/FeatureFlag';
import { useFeatureCanary } from 'utils/useFeatureCanary';
import { Resource } from 'Workspace/ResourceProvider/resourceTypes';

export const useResourceActions = (resource: Resource) => {
	const context = useResourceManagerContext();
	const [resourceSession, setResourceSession] = useState<Session>();
	const [resourceMachine, setResourceMachine] = useState<MachineData>();

	const isMachinePowerStateEnabled = useFeatureCanary(
		FeatureFlag.EnableMachinePowerState
	);
	const isDisconnectAndLogoutEnabled = useFeatureCanary(
		FeatureFlag.EnableDisconnectAndLogout
	);

	const actions = useDesktopActions({
		session: resourceSession,
		machine: resourceSession?.machineData || resourceMachine,
	});

	const isMachineQueryEnabled =
		!!resource?.canquerymachinestate && isMachinePowerStateEnabled;

	useEffect(() => {
		if (!resource.isdesktop) {
			return;
		}

		if (!isMachineQueryEnabled && !isDisconnectAndLogoutEnabled) {
			return;
		}

		const machine = context?.machineStatus?.machineData?.find(
			({ resourceId }) => resourceId === resource.id
		);
		setResourceMachine(machine ? machine.machineData : undefined);
	}, [
		context?.machineStatus?.machineData,
		resource.id,
		resource.isdesktop,
		isMachineQueryEnabled,
		isDisconnectAndLogoutEnabled,
	]);

	useEffect(() => {
		if (!resource.isdesktop) {
			return;
		}

		if (!isMachineQueryEnabled && !isDisconnectAndLogoutEnabled) {
			return;
		}

		const allSessions = [
			...context?.localSessions?.sessions,
			...context?.remoteSessions?.sessions,
			...context?.hibernatedSessions?.sessions,
		];
		const session = allSessions.find(
			({ applications }) => first(applications)?.resource?.id === resource.id
		);
		setResourceSession(session);
	}, [
		context?.localSessions?.sessions,
		context?.remoteSessions?.sessions,
		context?.hibernatedSessions?.sessions,
		resource,
		isMachineQueryEnabled,
		isDisconnectAndLogoutEnabled,
	]);

	const filteredActions = useMemo(() => {
		return (
			actions?.filter(
				action =>
					(isMachineQueryEnabled && isInEnum(action, MachineAction)) ||
					action === LaunchAction.RESUME ||
					(isDisconnectAndLogoutEnabled && isInEnum(action, SessionAction))
			) || []
		);
	}, [actions, isMachineQueryEnabled, isDisconnectAndLogoutEnabled]);

	return {
		session: resourceSession || {
			machineData: resourceMachine,
		},
		actions: filteredActions,
		machine: resourceSession?.machineData || resourceMachine,
	};
};
